import React from 'react';


const SITE_URL = 'https://www.nlsolution.org';


export const OrganizationSchema = () => {
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'EducationalOrganization',
        name: 'Northern Lights Solutions',
        alternateName: 'NLS',
        url: SITE_URL,
        logo: `${SITE_URL}/logo_green.png`,
        description: 'Character First. Education Always. Comprehensive character education curriculum for K-12 schools.',
        sameAs: [],
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            availableLanguage: ['English']
        },
        areaServed: {
            '@type': 'Place',
            name: 'Worldwide'
        }
    };

    return (
        <script type="application/ld+json">
            {JSON.stringify(schema)}
        </script>
    );
};

/**
 * WebSite Schema - Add to Layout.
 */
export const WebSiteSchema = () => {
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Northern Lights Solutions',
        url: SITE_URL,
    };

    return (
        <script type="application/ld+json">
            {JSON.stringify(schema)}
        </script>
    );
};

/**
 * BreadcrumbList Schema - Add to pages for navigation structure
 * @param {Array} items - Array of {name, path} objects
 */
export const BreadcrumbSchema = ({ items }) => {
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: `${SITE_URL}${item.path}`
        }))
    };

    return (
        <script type="application/ld+json">
            {JSON.stringify(schema)}
        </script>
    );
};

/**
 * WebPage Schema - Add to individual pages
 * @param {string} name - Page name
 * @param {string} description - Page description
 * @param {string} path - Page path
 */
export const WebPageSchema = ({ name, description, path }) => {
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: name,
        description: description,
        url: `${SITE_URL}${path}`,
        isPartOf: {
            '@type': 'WebSite',
            name: 'Northern Lights Solutions',
            url: SITE_URL
        },
        publisher: {
            '@type': 'EducationalOrganization',
            name: 'Northern Lights Solutions',
            logo: {
                '@type': 'ImageObject',
                url: `${SITE_URL}/logo_green.png`
            }
        }
    };

    return (
        <script type="application/ld+json">
            {JSON.stringify(schema)}
        </script>
    );
};

/**
 * Product Schema — for use on ProductDetail & BundleDetail pages.
 * Enables Google rich results for products with price, availability, etc.
 */
export const ProductSchema = ({ name, description, image, price, currency, availability, url, sku, brand = 'Northern Lights Solutions' }) => {
    const normalizedImage = typeof image === 'string' && image.trim() ? image : `${SITE_URL}/logo_green.png`;
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name,
        description,
        image: normalizedImage.startsWith('http') ? normalizedImage : `${SITE_URL}${normalizedImage}`,
        sku: sku || undefined,
        brand: { '@type': 'Brand', name: brand },
        offers: {
            '@type': 'Offer',
            url: `${SITE_URL}${url}`,
            priceCurrency: currency || 'USD',
            price: price ?? undefined,
            availability: availability === 'available'
                ? 'https://schema.org/InStock'
                : availability === 'out_of_stock'
                ? 'https://schema.org/OutOfStock'
                : 'https://schema.org/PreOrder',
            seller: { '@type': 'Organization', name: 'Northern Lights Solutions' },
        },
    };
    // Remove undefined price (e.g. "price on request" items)
    if (schema.offers.price === undefined) delete schema.offers.price;
    return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
};

/**
 * Article Schema — for use on BlogDetail pages.
 * Enables Google rich results for articles.
 */
export const ArticleSchema = ({ headline, description, image, datePublished, dateModified, authorName, url }) => {
    const normalizedImage = typeof image === 'string' && image.trim() ? image : `${SITE_URL}/logo_green.png`;
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline,
        description,
        image: normalizedImage.startsWith('http') ? normalizedImage : `${SITE_URL}${normalizedImage}`,
        datePublished,
        dateModified: dateModified || datePublished,
        author: { '@type': 'Person', name: authorName || 'NLS Team' },
        publisher: {
            '@type': 'Organization',
            name: 'Northern Lights Solutions',
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo_green.png` },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}${url}` },
    };
    return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
};

const structuredDataExports = {
    OrganizationSchema,
    WebSiteSchema,
    BreadcrumbSchema,
    WebPageSchema,
    ProductSchema,
    ArticleSchema,
};

export default structuredDataExports;
